import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IdCard, UserCircle2, Upload } from "lucide-react";
import GlassCard from "../../components/ui-custom/GlassCard";
import Reveal from "../../components/ui-custom/Reveal";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Resizes/compresses the chosen image client-side (max 320px, JPEG) before
// it's sent as a data: URL - keeps the payload well under Firestore's 1MB
// document limit without needing a separate object-storage bucket.
const resizeImageToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const maxDim = 320;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: "", employee_code: "", department: "", role: "", phone: "" });
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [profileExists, setProfileExists] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/employees/profile`, { withCredentials: true })
      .then((res) => {
        if (res.data.exists) {
          setForm({
            name: res.data.name || "",
            employee_code: res.data.employee_code || "",
            department: res.data.department || "",
            role: res.data.role || "",
            phone: res.data.phone || "",
          });
          setPhotoDataUrl(res.data.photo_data_url || "");
          setProfileExists(true);
        } else {
          setForm((f) => ({ ...f, name: user?.name || "" }));
        }
      })
      .finally(() => setLoaded(true));
  }, [user]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError("");
    setUploadingPhoto(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      await axios.post(`${API}/employees/profile/photo`, { photo_data_url: dataUrl }, { withCredentials: true });
      setPhotoDataUrl(dataUrl);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0]?.msg : detail;
      setPhotoError(message || "Could not upload that photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.employee_code.trim()) {
      setError("Name and Employee Code are required.");
      return;
    }
    if (!/^\d{5}$/.test(form.employee_code.trim())) {
      setError("Employee Code must be exactly 5 digits (e.g. 00231).");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/employees/profile`, form, { withCredentials: true });
      setSaved(true);
      setProfileExists(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0]?.msg : detail;
      setError(message || "Could not save your profile. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-xl">
      <h2 className="font-display font-semibold text-2xl text-foreground mb-2">My Profile</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Complete this once to unlock the Employee Directory - your Employee Code confirms you as a verified
        team member. A photo is required too, so people are recognizable in the Directory and in any RACI
        listing you're added to.
      </p>
      <Reveal>
        <GlassCard className="p-7">
          <div className="flex items-center gap-5 mb-6">
            {photoDataUrl ? (
              <img
                src={photoDataUrl}
                alt="Your profile"
                className="w-20 h-20 rounded-full object-cover border border-border/20 flex-none"
              />
            ) : (
              <span className="w-20 h-20 rounded-full flex items-center justify-center bg-qcyan/10 border border-qcyan/25 flex-none">
                <UserCircle2 size={32} className="text-qcyan" />
              </span>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
                className="hidden"
                id="profile-photo-input"
              />
              <Button
                type="button"
                variant="outline"
                className="gap-1.5"
                disabled={uploadingPhoto || !profileExists}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={14} /> {uploadingPhoto ? "Uploading…" : photoDataUrl ? "Change Photo" : "Add Photo"}
              </Button>
              {!profileExists && (
                <p className="text-xs text-muted-foreground mt-2">Save your profile below first, then add a photo.</p>
              )}
              {profileExists && !photoDataUrl && (
                <p className="text-xs text-destructive mt-2">Required to unlock the Directory.</p>
              )}
              {photoError && <p className="text-xs text-destructive mt-2">{photoError}</p>}
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Full Name
              </label>
              <Input value={form.name} onChange={update("name")} placeholder="Your full name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block flex items-center gap-1.5">
                <IdCard size={13} /> Employee Code
              </label>
              <Input
                value={form.employee_code}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 5);
                  setForm((f) => ({ ...f, employee_code: digitsOnly }));
                }}
                inputMode="numeric"
                maxLength={5}
                placeholder="e.g. 00231"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Department
              </label>
              <Input value={form.department} onChange={update("department")} placeholder="e.g. Sales, R&D, Manufacturing" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Role / Title
              </label>
              <Input value={form.role} onChange={update("role")} placeholder="e.g. Senior Design Engineer" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Phone
              </label>
              <Input value={form.phone} onChange={update("phone")} placeholder="Optional" />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {saved && <p className="text-sm text-qcyan">Profile saved.</p>}

            <div className="flex gap-3">
              <Button type="submit" className="btn-glow-primary" disabled={submitting}>
                {submitting ? "Saving…" : "Save Profile"}
              </Button>
              {saved && (
                <Button type="button" variant="outline" onClick={() => navigate("/employees/directory")}>
                  View Directory
                </Button>
              )}
            </div>
          </form>
        </GlassCard>
      </Reveal>
    </div>
  );
};

export default Profile;
